import { useState, useCallback, useRef } from 'preact/hooks'
import type { WebsterChatMessage, WebsterAttachment } from '@/types/webster'

export interface UseWebsterChatResult {
    messages: WebsterChatMessage[]
    generating: boolean
    hasMessages: boolean
    /**
     * Adds a user message to the chat. The optional `options.createdByName`
     * attributes the bubble to a specific user — used when the message came
     * from a SignalR broadcast for a peer in a shared asset-chat session.
     * Omit for the local user; the bubble will render unattributed.
     */
    addUserMessage: (content: string, options?: { createdByName?: string | null; attachments?: WebsterAttachment[] }) => void
    /**
     * Adds a finalized assistant message. The optional `options.modelId` +
     * `options.outputTokens` drive the per-message MessageTokenFooter render
     * — set them from the SignalR event payload (conversationalResponse /
     * assistantMessageAppended) so the footer appears live, not just after
     * a chat reload. Omit when no attribution is available yet.
     */
    addAssistantMessage: (content: string, options?: { modelId?: string | null; outputTokens?: number | null }) => void
    /** Add or update a streaming assistant message (upserts by id) */
    updateStreamingMessage: (id: string, update: Partial<WebsterChatMessage>) => void
    /** Remove stream status from a message (generation complete) */
    finalizeStreamingMessage: (id: string) => void
    /** Remove a message entirely (for transient working-indicator bubbles) */
    removeMessage: (id: string) => void
    setGenerating: (value: boolean) => void
    initializeFromHistory: (messages: WebsterChatMessage[]) => void
    clear: () => void
}

export function useWebsterChat(): UseWebsterChatResult {
    const [messages, setMessages] = useState<WebsterChatMessage[]>([])
    const [generating, setGenerating] = useState(false)
    const messageCounter = useRef(0)

    const addUserMessage = useCallback((content: string, options?: { createdByName?: string | null; attachments?: WebsterAttachment[] }) => {
        const msg: WebsterChatMessage = {
            id: `user-${++messageCounter.current}`,
            role: 'user',
            content,
            timestamp: new Date(),
            createdByName: options?.createdByName ?? null,
            attachments: options?.attachments && options.attachments.length > 0 ? options.attachments : undefined,
        }
        setMessages(prev => [...prev, msg])
    }, [])

    const addAssistantMessage = useCallback((
        content: string,
        options?: { modelId?: string | null; outputTokens?: number | null },
    ) => {
        const msg: WebsterChatMessage = {
            id: `assistant-${++messageCounter.current}`,
            role: 'assistant',
            content,
            timestamp: new Date(),
            modelId: options?.modelId ?? null,
            outputTokens: options?.outputTokens ?? null,
        }
        setMessages(prev => [...prev, msg])
    }, [])

    const updateStreamingMessage = useCallback((id: string, update: Partial<WebsterChatMessage>) => {
        setMessages(prev => {
            const idx = prev.findIndex(m => m.id === id)
            if (idx >= 0) {
                const updated = [...prev]
                updated[idx] = { ...updated[idx], ...update }
                return updated
            }
            // Message doesn't exist yet — create it
            return [...prev, {
                id,
                role: 'assistant' as const,
                content: '',
                timestamp: new Date(),
                ...update,
            }]
        })
    }, [])

    const finalizeStreamingMessage = useCallback((id: string) => {
        setMessages(prev => prev.map(m =>
            m.id === id ? { ...m, streamStatus: undefined } : m
        ))
    }, [])

    const removeMessage = useCallback((id: string) => {
        setMessages(prev => prev.filter(m => m.id !== id))
    }, [])

    const initializeFromHistory = useCallback((msgs: WebsterChatMessage[]) => {
        setMessages(msgs)
    }, [])

    const clear = useCallback(() => {
        setMessages([])
        setGenerating(false)
    }, [])

    return {
        messages,
        generating,
        hasMessages: messages.length > 0,
        addUserMessage,
        addAssistantMessage,
        updateStreamingMessage,
        finalizeStreamingMessage,
        removeMessage,
        setGenerating,
        initializeFromHistory,
        clear,
    }
}
